import {
  MIN_USD_PRICE,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import { ESaleDiscountDisplayType } from 'constants/enums';
import { z } from 'zod';

const ONE_DECIMAL_REGEX = /^\d+(\.\d{1})?$/;

export const generalFieldsSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
      message: 'Name should not be spaces'
    }),
  displayName: z.string().optional().default(''),
  description: z
    .string()
    .optional()
    .default('')
    .refine((value) => !value || value.length >= 3, {
      message: 'Description length should be at least 3 characters'
    }),
  publisherOfferId: z
    .string()
    .min(1, 'External ID is required')
    .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
      message: 'External ID should not be spaces'
    }),
  offerUiId: z.string().min(1, 'Offer Design is required'),
  price: z.number().min(0),
  section: z.string().optional().default('0'),
  tab: z.string().optional().default('0')
});

export const saleDiscountFieldsSchema = z.object({
  salePercentage: z
    .number()
    .min(0, 'Sale amount cannot be negative')
    .refine(
      (value) => value === undefined || ONE_DECIMAL_REGEX.test(value.toString()),
      { message: 'Sale amount can have one decimal place at most' }
    )
    .optional()
    .default(0),
  salePercentageDisplayType: z
    .string()
    .optional()
    .default(ESaleDiscountDisplayType.PERCENTAGE),
  discount: z
    .number()
    .min(0, 'Price discount cannot be negative')
    .max(99, 'Price discount cannot be more than 99%')
    .refine(
      (value) => value === undefined || ONE_DECIMAL_REGEX.test(value.toString()),
      { message: 'Price discount can have one decimal place at most' }
    )
    .optional()
    .default(0),
  priceDiscountDisplayType: z
    .string()
    .optional()
    .default(ESaleDiscountDisplayType.PERCENTAGE),
  priceBeforeDiscount: z
    .number()
    .min(0, 'Crossed price cannot be negative')
    .nullable()
    .optional()
});

export const productsFieldsSchema = z.object({
  products: z.array(z.any()).default([])
});

export const tagsFieldsSchema = z.object({
  tags: z.array(z.string()).default([])
});

export const segmentsFieldsSchema = z.object({
  segments: z.array(z.string()).default([])
});

export const badgeFieldsSchema = z.object({
  badge: z.string().nullable().optional(),
  publisherBadgeId: z.string().optional().default('')
});

export const standardPriceRefinement = <T extends z.ZodTypeAny>(
  schema: T
): z.ZodEffects<T> =>
  schema.refine(
    (data: { setAsFree?: boolean; price?: number }) =>
      data.setAsFree === true || (data.price != null && data.price >= MIN_USD_PRICE),
    {
      path: ['price'],
      message: `Price must be at least ${MIN_USD_PRICE}`
    }
  ) as z.ZodEffects<T>;
